/**
 * Master Claims Tab (Tab 2)
 * 3-step scan-and-create workflow: Upload → Review Extracted Data → Create Claim
 * Also supports manual entry without document scanning.
 * Detail view includes portal submission tracking and visit line items.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FileText,
  Plus,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  ArrowLeft,
  AlertTriangle,
  Clock,
  Loader2,
  User,
  Calendar,
  Trash2,
  FileCheck,
  CreditCard,
  Baby,
  Activity,
  Send,
  Search,
  Upload,
  Globe,
  Edit3,
} from 'lucide-react';
import documentProcessingApi, {
  type MasterClaim,
  type MasterClaimStatus,
  type MasterClaimLineItem,
  type MasterClaimCreate,
  type MasterClaimLineItemCreate,
  type MasterClaimUploadResult,
  type ScanExtractedFields,
} from '@/services/api/documentProcessing';
import { enrollmentsAPI, type MemberSearchResult } from '@/services/api/enrollments';
import { formatDateOnly } from '@/utils/dateTime';

interface MasterClaimsTabProps {
  onNavigate?: (tab: string) => void;
  onSwitchTab?: (tab: string) => void;
}

const CLAIM_STATUS_CONFIG: Record<MasterClaimStatus, { label: string; color: string; icon: React.ReactNode }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: <Clock className="w-3 h-3" /> },
  ready: { label: 'Ready', color: 'bg-blue-100 text-blue-700', icon: <CheckCircle className="w-3 h-3" /> },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
  submitted: { label: 'Submitted', color: 'bg-purple-100 text-purple-700', icon: <Send className="w-3 h-3" /> },
  claim_created: { label: 'Claim Created', color: 'bg-emerald-100 text-emerald-700', icon: <FileCheck className="w-3 h-3" /> },
};

const LINE_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  prenatal_visit: { label: 'Prenatal Visit', icon: <Activity className="w-3 h-3" />, color: 'text-blue-700 bg-blue-50' },
  postpartum_visit: { label: 'Postpartum Visit', icon: <Activity className="w-3 h-3" />, color: 'text-purple-700 bg-purple-50' },
  delivery: { label: 'Delivery', icon: <Baby className="w-3 h-3" />, color: 'text-pink-700 bg-pink-50' },
};

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  vaginal: 'Vaginal',
  c_section: 'C-Section',
  vbac: 'VBAC',
};

const SERVICE_TYPE_OPTIONS = [
  { value: 'prenatal_visit', label: 'Prenatal Visit' },
  { value: 'postpartum_visit', label: 'Postpartum Visit' },
  { value: 'delivery', label: 'Labor & Delivery' },
];

// Map OCR-extracted service type text to our internal values
function normalizeServiceType(raw: string | null): string {
  if (!raw) return 'prenatal_visit';
  const lower = raw.toLowerCase().trim();
  if (lower.includes('postpartum') || lower.includes('postnatal') || lower.includes('post partum') || lower.includes('post natal')) return 'postpartum_visit';
  if (lower.includes('labor') || lower.includes('labour') || lower.includes('delivery') || lower.includes('l&d') || lower.includes('l & d')) return 'delivery';
  return 'prenatal_visit';
}

export default function MasterClaimsTab({ onNavigate, onSwitchTab }: MasterClaimsTabProps) {
  // View state
  const [view, setView] = useState<'list' | 'detail' | 'create'>('list');
  const [createMode, setCreateMode] = useState<'scan' | 'manual'>('scan');
  const [createStep, setCreateStep] = useState<'upload' | 'review' | 'done'>('upload');

  // List state
  const [items, setItems] = useState<MasterClaim[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Detail state
  const [selectedClaim, setSelectedClaim] = useState<MasterClaim | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState<MasterClaimCreate>({
    member_id: 0,
    due_date: '',
    health_plan: '',
    internal_notes: '',
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState<MemberSearchResult[]>([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberSearchResult | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Scan extraction state
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<MasterClaimUploadResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extractedFields, setExtractedFields] = useState<ScanExtractedFields>({
    date_of_service: null,
    service_type: null,
    due_date: null,
    health_plan: null,
    member_cin: null,
    member_plan_id: null,
    plan_name: null,
    doula_name: null,
    duration_minutes: null,
  });
  const [editServiceType, setEditServiceType] = useState<string>('prenatal_visit');
  const [editDateOfService, setEditDateOfService] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editHealthPlan, setEditHealthPlan] = useState('');

  // Add line item state
  const [showAddLine, setShowAddLine] = useState(false);
  const [newLine, setNewLine] = useState<MasterClaimLineItemCreate>({
    line_type: 'prenatal_visit',
    service_date: '',
  });

  // ---- Data Loading ----

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await documentProcessingApi.listMasterClaims({
        status: statusFilter || undefined,
        limit: 50,
      });
      setItems(resp.items);
      setTotal(resp.total);
    } catch (err) {
      console.error('Failed to load master claims:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await documentProcessingApi.getMasterClaim(id);
      setSelectedClaim(data);
    } catch (err) {
      console.error('Failed to load master claim detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ---- Actions ----

  const handleCreate = async () => {
    if (!createForm.member_id) {
      alert('Please select a member');
      return;
    }
    setActionLoading(true);
    try {
      const claim = await documentProcessingApi.createMasterClaim(createForm);

      // If from scan, auto-add initial line item from extracted data
      if (createMode === 'scan' && editDateOfService) {
        try {
          await documentProcessingApi.addMasterClaimLineItem(claim.id, {
            line_type: editServiceType,
            service_date: editDateOfService,
          });
        } catch (lineErr) {
          console.warn('Could not auto-add initial line item:', lineErr);
        }
      }

      setView('detail');
      loadDetail(claim.id);
      loadList();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create master claim');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddLineItem = async () => {
    if (!selectedClaim || !newLine.service_date) {
      alert('Service date is required');
      return;
    }
    if (newLine.line_type === 'delivery' && !newLine.delivery_type) {
      alert('Delivery type is required for delivery entries');
      return;
    }
    setActionLoading(true);
    try {
      await documentProcessingApi.addMasterClaimLineItem(selectedClaim.id, newLine);
      setShowAddLine(false);
      setNewLine({ line_type: 'prenatal_visit', service_date: '' });
      loadDetail(selectedClaim.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to add line item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLineItem = async (lineId: number) => {
    if (!selectedClaim) return;
    if (!confirm('Delete this line item?')) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.deleteMasterClaimLineItem(selectedClaim.id, lineId);
      loadDetail(selectedClaim.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete line item');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async (status: string) => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.updateMasterClaim(selectedClaim.id, { status });
      loadDetail(selectedClaim.id);
      loadList();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToClaim = async () => {
    if (!selectedClaim) return;
    if (!confirm('Convert this master claim to a draft claim in Claims Management?')) return;
    setActionLoading(true);
    try {
      const result = await documentProcessingApi.convertMasterClaimToClaim(selectedClaim.id);
      if (result.success) {
        alert(`Draft claim ${result.claim_number} created successfully!`);
        loadDetail(selectedClaim.id);
        loadList();
      } else {
        alert(result.error || 'Conversion failed');
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this master claim?')) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.deleteMasterClaim(id);
      if (view === 'detail') {
        backToList();
      } else {
        loadList();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortalToggle = async (checked: boolean) => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.updateMasterClaim(selectedClaim.id, {
        submitted_on_portal: checked,
        portal_submission_date: checked ? new Date().toISOString().slice(0, 10) : undefined,
      });
      loadDetail(selectedClaim.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update portal submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortalDateChange = async (date: string) => {
    if (!selectedClaim) return;
    setActionLoading(true);
    try {
      await documentProcessingApi.updateMasterClaim(selectedClaim.id, {
        portal_submission_date: date,
      });
      loadDetail(selectedClaim.id);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update portal date');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetail = (id: number) => {
    loadDetail(id);
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelectedClaim(null);
    setShowAddLine(false);
    setSelectedMember(null);
    setMemberSearch('');
    setMemberSearchResults([]);
    setUploadResult(null);
    setCreateStep('upload');
    setCreateMode('scan');
    setCreateForm({ member_id: 0, due_date: '', health_plan: '', internal_notes: '' });
    setExtractedFields({
      date_of_service: null, service_type: null, due_date: null,
      health_plan: null, member_cin: null, member_plan_id: null,
      plan_name: null, doula_name: null, duration_minutes: null,
    });
    loadList();
  };

  // ---- Member Search ----

  const handleMemberSearch = (query: string) => {
    setMemberSearch(query);
    setSelectedMember(null);
    setCreateForm(f => ({ ...f, member_id: 0 }));

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!query.trim() || query.length < 2) {
      setMemberSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setMemberSearchLoading(true);
      try {
        const resp = await enrollmentsAPI.searchMembers({ search: query, limit: 10 });
        setMemberSearchResults(resp.members);
      } catch (err) {
        console.error('Member search failed:', err);
      } finally {
        setMemberSearchLoading(false);
      }
    }, 300);
  };

  const handleSelectMember = (member: MemberSearchResult) => {
    setSelectedMember(member);
    setCreateForm(f => ({ ...f, member_id: member.id }));
    setMemberSearch(`${member.first_name} ${member.last_name}`);
    setMemberSearchResults([]);
  };

  // ---- Document Upload (Scan & Extract) ----

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await documentProcessingApi.scanAndExtractMasterClaim(file);
      setUploadResult(result);
      setExtractedFields(result.extracted_fields);

      // Pre-fill editable fields from extraction
      setEditDateOfService(result.extracted_fields.date_of_service || '');
      setEditServiceType(normalizeServiceType(result.extracted_fields.service_type));
      setEditDueDate(result.extracted_fields.due_date || '');
      setEditHealthPlan(result.extracted_fields.health_plan || result.extracted_fields.plan_name || '');

      // Pre-fill create form
      setCreateForm(f => ({
        ...f,
        due_date: result.extracted_fields.due_date || f.due_date,
        health_plan: result.extracted_fields.health_plan || result.extracted_fields.plan_name || f.health_plan,
        source_queue_item_id: result.queue_item_id,
      }));

      // If member matched, pre-fill
      if (result.member_id && result.member_name) {
        const nameParts = result.member_name.split(' ');
        const first = nameParts[0] || '';
        const last = nameParts.slice(1).join(' ') || '';
        setSelectedMember({ id: result.member_id, first_name: first, last_name: last });
        setMemberSearch(result.member_name);
        setCreateForm(f => ({ ...f, member_id: result.member_id! }));
      }

      // Move to review step
      setCreateStep('review');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Document upload and processing failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---- Member Search Dropdown Component ----

  const renderMemberSearchDropdown = () => (
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-1">Member *</label>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search by name, Medicaid ID..."
          value={memberSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMemberSearch(e.target.value)}
          className="pl-9"
        />
        {memberSearchLoading && (
          <Loader2 className="absolute right-2.5 top-2.5 w-4 h-4 animate-spin text-gray-400" />
        )}
      </div>

      {memberSearchResults.length > 0 && (
        <div className="border rounded-md mt-1 max-h-48 overflow-y-auto bg-white shadow-lg z-10 relative">
          {memberSearchResults.map((m) => (
            <button
              key={m.id}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between text-sm border-b last:border-b-0"
              onClick={() => handleSelectMember(m)}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400" />
                <span>{m.first_name} {m.last_name}</span>
              </div>
              <span className="text-xs text-gray-500">
                {m.date_of_birth ? `DOB: ${m.date_of_birth}` : `ID: ${m.id}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {selectedMember && (
        <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm">
          <User className="w-4 h-4 text-blue-600" />
          <span className="font-medium">{selectedMember.first_name} {selectedMember.last_name}</span>
          {selectedMember.date_of_birth && (
            <span className="text-xs text-gray-500">DOB: {selectedMember.date_of_birth}</span>
          )}
          <button
            onClick={() => {
              setSelectedMember(null);
              setMemberSearch('');
              setCreateForm(f => ({ ...f, member_id: 0 }));
            }}
            className="ml-auto text-gray-400 hover:text-red-500"
          >
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );

  // ---- List View ----

  const renderList = () => {
    const statusTabs = [
      { value: '', label: 'All' },
      { value: 'draft', label: 'Draft' },
      { value: 'ready', label: 'Ready' },
      { value: 'approved', label: 'Approved' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'claim_created', label: 'Claim Created' },
    ];

    return (
      <>
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            {statusTabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadList()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button size="sm" onClick={() => setView('create')}>
              <Plus className="w-4 h-4 mr-1" />
              New Master Claim
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium text-gray-600">Member</th>
                  <th className="text-left p-3 font-medium text-gray-600">Due Date</th>
                  <th className="text-left p-3 font-medium text-gray-600">Health Plan</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-left p-3 font-medium text-gray-600">Visits</th>
                  <th className="text-left p-3 font-medium text-gray-600">Insurance</th>
                  <th className="text-left p-3 font-medium text-gray-600">Portal</th>
                  <th className="text-left p-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading master claims...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center p-8 text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No master claims found. Create a new master claim to get started.
                    </td>
                  </tr>
                ) : (
                  items.map((claim) => {
                    const statusCfg = CLAIM_STATUS_CONFIG[claim.status] || CLAIM_STATUS_CONFIG.draft;

                    return (
                      <tr
                        key={claim.id}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => openDetail(claim.id)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="font-medium text-gray-900">
                                {claim.member_name || `Member #${claim.member_id}`}
                              </div>
                              {claim.member_cin && (
                                <div className="text-xs text-gray-400">CIN: {claim.member_cin}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-600">
                            {claim.due_date ? new Date(claim.due_date).toLocaleDateString() : '--'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-600">
                            {claim.health_plan || '--'}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            {statusCfg.icon}
                            {statusCfg.label}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm font-medium">{claim.total_visits + claim.total_deliveries}</span>
                        </td>
                        <td className="p-3">
                          {claim.has_insurance_documents ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                              <CheckCircle className="w-3 h-3" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-yellow-700">
                              <AlertTriangle className="w-3 h-3" />
                              Missing
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          {claim.submitted_on_portal ? (
                            <span className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-full">
                              <Globe className="w-3 h-3" />
                              Portal
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">--</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                openDetail(claim.id);
                              }}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {!claim.claim_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  handleDelete(claim.id);
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {total > items.length && (
            <div className="p-3 border-t text-center text-sm text-gray-500">
              Showing {items.length} of {total} claims
            </div>
          )}
        </Card>
      </>
    );
  };

  // ---- Create View (3-step scan workflow + manual entry) ----

  const renderCreate = () => (
    <>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={backToList}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>
        <h2 className="text-lg font-semibold">New Master Claim</h2>
      </div>

      {/* Mode toggle: Scan Document vs Manual Entry */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => { setCreateMode('scan'); setCreateStep('upload'); }}
          className={`px-4 py-2 text-sm rounded-lg transition-colors ${
            createMode === 'scan'
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-100 border'
          }`}
        >
          <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Scan Document
        </button>
        <button
          onClick={() => setCreateMode('manual')}
          className={`px-4 py-2 text-sm rounded-lg transition-colors ${
            createMode === 'manual'
              ? 'bg-blue-100 text-blue-700 font-medium'
              : 'text-gray-600 hover:bg-gray-100 border'
          }`}
        >
          <Edit3 className="w-4 h-4 inline mr-1.5 -mt-0.5" />
          Manual Entry
        </button>
      </div>

      {createMode === 'scan' ? renderCreateScan() : renderCreateManual()}
    </>
  );

  // ---- Scan Document Create Flow ----

  const renderCreateScan = () => {
    if (createStep === 'upload') {
      return (
        <Card className="p-6 max-w-xl">
          <div className="text-center py-8">
            <Upload className="w-12 h-12 mx-auto mb-4 text-blue-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upload Scanned Document
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
              Upload a multi-page document containing the insurance/Medicaid ID card and doula activity form with encounter notes.
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              size="sm"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing document...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Choose File
                </>
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.bmp"
              onChange={handleDocumentUpload}
              className="hidden"
            />
            <p className="text-xs text-gray-400 mt-3">
              Accepted: PDF, JPG, PNG, TIFF, BMP (max 20MB)
            </p>
          </div>
        </Card>
      );
    }

    if (createStep === 'review') {
      return (
        <div className="max-w-2xl space-y-4">
          {/* Member Match Banner */}
          {uploadResult && (
            <div className={`flex items-start gap-3 p-4 rounded-lg ${
              uploadResult.member_id
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              {uploadResult.member_id ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Member Matched: {uploadResult.member_name}
                    </p>
                    {uploadResult.match_method && (
                      <p className="text-xs text-green-600 mt-0.5">
                        Match method: {uploadResult.match_method}
                        {uploadResult.match_confidence && ` (${Math.round(uploadResult.match_confidence * 100)}% confidence)`}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">
                      No Matching Member Found
                    </p>
                    <p className="text-xs text-yellow-600 mt-0.5">
                      Search and select a member below to continue.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Extracted Fields (Editable) */}
          <Card className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Extracted Data — Review & Edit
            </h3>

            <div className="space-y-4">
              {/* Member */}
              {renderMemberSearchDropdown()}

              {/* Date of Service */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Date of Service</label>
                <Input
                  type="date"
                  value={editDateOfService}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditDateOfService(e.target.value)}
                />
              </div>

              {/* Service Type */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Service Type</label>
                <select
                  value={editServiceType}
                  onChange={(e) => setEditServiceType(e.target.value)}
                  className="w-full h-9 text-sm border rounded-md px-3 bg-white"
                >
                  {SERVICE_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Due Date */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Due Date (EDD)</label>
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEditDueDate(e.target.value);
                    setCreateForm(f => ({ ...f, due_date: e.target.value }));
                  }}
                />
              </div>

              {/* Health Plan */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Health Plan</label>
                <Input
                  placeholder="Health plan name..."
                  value={editHealthPlan}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEditHealthPlan(e.target.value);
                    setCreateForm(f => ({ ...f, health_plan: e.target.value }));
                  }}
                />
              </div>

              {/* Insurance Card Fields (read-only reference) */}
              {(extractedFields.member_cin || extractedFields.member_plan_id || extractedFields.plan_name) && (
                <div className="pt-3 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    Insurance Card Data (Reference)
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    {extractedFields.member_cin && (
                      <div>
                        <span className="text-xs text-gray-400">CIN</span>
                        <div className="text-sm font-medium">{extractedFields.member_cin}</div>
                      </div>
                    )}
                    {extractedFields.member_plan_id && (
                      <div>
                        <span className="text-xs text-gray-400">Member ID</span>
                        <div className="text-sm font-medium">{extractedFields.member_plan_id}</div>
                      </div>
                    )}
                    {extractedFields.plan_name && (
                      <div>
                        <span className="text-xs text-gray-400">Plan Name</span>
                        <div className="text-sm font-medium">{extractedFields.plan_name}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Doula info (read-only) */}
              {(extractedFields.doula_name || extractedFields.duration_minutes) && (
                <div className="pt-3 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                    Encounter Data (Reference)
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {extractedFields.doula_name && (
                      <div>
                        <span className="text-xs text-gray-400">Doula Name</span>
                        <div className="text-sm font-medium">{extractedFields.doula_name}</div>
                      </div>
                    )}
                    {extractedFields.duration_minutes && (
                      <div>
                        <span className="text-xs text-gray-400">Duration</span>
                        <div className="text-sm font-medium">{extractedFields.duration_minutes} min</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Internal Notes</label>
                <Input
                  placeholder="Optional notes..."
                  value={createForm.internal_notes || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCreateForm((f) => ({ ...f, internal_notes: e.target.value }))
                  }
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 mt-4 border-t">
              <Button onClick={handleCreate} disabled={actionLoading || !createForm.member_id}>
                {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Create Master Claim
              </Button>
              <Button variant="outline" onClick={() => setCreateStep('upload')}>
                Re-scan
              </Button>
              <Button variant="outline" onClick={backToList}>Cancel</Button>
            </div>
          </Card>
        </div>
      );
    }

    return null;
  };

  // ---- Manual Entry Create Flow ----

  const renderCreateManual = () => (
    <Card className="p-6 max-w-xl">
      <div className="space-y-4">
        {renderMemberSearchDropdown()}

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Due Date</label>
          <Input
            type="date"
            value={createForm.due_date || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCreateForm((f) => ({ ...f, due_date: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Health Plan</label>
          <Input
            placeholder="Health plan name..."
            value={createForm.health_plan || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCreateForm((f) => ({ ...f, health_plan: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Internal Notes</label>
          <Input
            placeholder="Optional notes..."
            value={createForm.internal_notes || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCreateForm((f) => ({ ...f, internal_notes: e.target.value }))
            }
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button onClick={handleCreate} disabled={actionLoading || !createForm.member_id}>
            {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Create Master Claim
          </Button>
          <Button variant="outline" onClick={backToList}>Cancel</Button>
        </div>
      </div>
    </Card>
  );

  // ---- Detail View ----

  const renderDetail = () => {
    if (detailLoading || !selectedClaim) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      );
    }

    const statusCfg = CLAIM_STATUS_CONFIG[selectedClaim.status] || CLAIM_STATUS_CONFIG.draft;
    const lineItems = selectedClaim.line_items || [];
    const isTerminal = selectedClaim.status === 'claim_created';
    const canConvert = (selectedClaim.status === 'approved' || selectedClaim.status === 'ready') && !selectedClaim.claim_id;

    return (
      <>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={backToList}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <h2 className="text-lg font-semibold">
              {selectedClaim.member_name || `Member #${selectedClaim.member_id}`}
            </h2>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusCfg.color}`}>
              {statusCfg.icon}
              {statusCfg.label}
            </span>
            {selectedClaim.flagged && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <AlertTriangle className="w-3 h-3" />
                Flagged
              </span>
            )}
            {selectedClaim.submitted_on_portal && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                <Globe className="w-3 h-3" />
                Portal
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!isTerminal && selectedClaim.status === 'draft' && (
              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate('ready')} disabled={actionLoading}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark Ready
              </Button>
            )}
            {selectedClaim.status === 'ready' && (
              <Button size="sm" variant="outline" onClick={() => handleStatusUpdate('approved')} disabled={actionLoading}>
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
            )}
            {canConvert && (
              <Button size="sm" onClick={handleConvertToClaim} disabled={actionLoading || !selectedClaim.has_insurance_documents}>
                {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileCheck className="w-4 h-4 mr-1" />}
                Convert to Draft Claim
              </Button>
            )}
            {!isTerminal && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(selectedClaim.id)}
                disabled={actionLoading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Insurance Warning */}
        {!selectedClaim.has_insurance_documents && (
          <Card className="p-4 mb-4 bg-yellow-50 border-yellow-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">Missing Insurance Documents</p>
                  <p className="text-sm text-yellow-600">
                    This member does not have insurance cards on file. Upload insurance cards before converting to a claim.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSwitchTab?.('member-profiles')}
                className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              >
                <CreditCard className="w-4 h-4 mr-1" />
                Upload Insurance Cards
              </Button>
            </div>
          </Card>
        )}

        {/* Member Info + Due Date + Health Plan */}
        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <span className="text-xs text-gray-500">Member</span>
              <div className="flex items-center gap-1 mt-1">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{selectedClaim.member_name || `#${selectedClaim.member_id}`}</span>
              </div>
            </div>
            {selectedClaim.member_cin && (
              <div>
                <span className="text-xs text-gray-500">CIN</span>
                <div className="font-medium mt-1">{selectedClaim.member_cin}</div>
              </div>
            )}
            {selectedClaim.member_plan_id && (
              <div>
                {/* TRUS-725: the plan's own name, not a hard-coded SFHP. */}
                <span className="text-xs text-gray-500">
                  {selectedClaim.health_plan ? `${selectedClaim.health_plan} Member ID` : 'Plan Member ID'}
                </span>
                <div className="font-medium mt-1">{selectedClaim.member_plan_id}</div>
              </div>
            )}
            <div>
              <span className="text-xs text-gray-500">Due Date</span>
              <div className="flex items-center gap-1 mt-1">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="font-medium">
                  {selectedClaim.due_date ? new Date(selectedClaim.due_date).toLocaleDateString() : 'Not set'}
                </span>
              </div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Health Plan</span>
              <div className="font-medium mt-1">{selectedClaim.health_plan || 'Not set'}</div>
            </div>
            <div>
              <span className="text-xs text-gray-500">Insurance</span>
              <div className="mt-1">
                {selectedClaim.has_insurance_documents ? (
                  <span className="inline-flex items-center gap-1 text-sm text-green-700">
                    <CheckCircle className="w-4 h-4" /> On file
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm text-yellow-700">
                    <AlertTriangle className="w-4 h-4" /> Missing
                  </span>
                )}
              </div>
            </div>
          </div>
          {selectedClaim.internal_notes && (
            <div className="mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">Notes</span>
              <p className="text-sm text-gray-700 mt-1">{selectedClaim.internal_notes}</p>
            </div>
          )}
        </Card>

        {/* Portal Submission Tracking */}
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-indigo-500" />
              <div>
                <span className="text-sm font-medium text-gray-700">Submitted Directly on Plan Website</span>
                <p className="text-xs text-gray-500">Mark if this claim was submitted directly through the health plan portal</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {selectedClaim.submitted_on_portal && selectedClaim.portal_submission_date && (
                <Input
                  type="date"
                  value={selectedClaim.portal_submission_date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePortalDateChange(e.target.value)}
                  className="w-40 h-8 text-sm"
                  disabled={actionLoading}
                />
              )}
              <button
                onClick={() => handlePortalToggle(!selectedClaim.submitted_on_portal)}
                disabled={actionLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  selectedClaim.submitted_on_portal ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    selectedClaim.submitted_on_portal ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </Card>

        {/* Visit Line Items */}
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Visit Line Items ({lineItems.length})
            </h3>
            {!isTerminal && (
              <Button size="sm" variant="outline" onClick={() => setShowAddLine(true)} disabled={showAddLine}>
                <Plus className="w-4 h-4 mr-1" />
                Add Visit
              </Button>
            )}
          </div>

          {/* Add line item form */}
          {showAddLine && (
            <div className="border rounded-md p-3 mb-3 bg-blue-50 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Visit Type *</label>
                  <select
                    value={newLine.line_type}
                    onChange={(e) => setNewLine((l) => ({
                      ...l,
                      line_type: e.target.value,
                      delivery_type: e.target.value === 'delivery' ? 'vaginal' : undefined,
                    }))}
                    className="w-full h-8 text-sm border rounded-md px-2 bg-white"
                  >
                    <option value="prenatal_visit">Prenatal Visit</option>
                    <option value="postpartum_visit">Postpartum Visit</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">
                    {newLine.line_type === 'delivery' ? 'Delivery Date *' : 'Date of Service *'}
                  </label>
                  <Input
                    type="date"
                    value={newLine.service_date}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewLine((l) => ({ ...l, service_date: e.target.value }))
                    }
                    className="h-8 text-sm"
                  />
                </div>
                {newLine.line_type === 'delivery' && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Delivery Type *</label>
                    <select
                      value={newLine.delivery_type || 'vaginal'}
                      onChange={(e) => setNewLine((l) => ({ ...l, delivery_type: e.target.value }))}
                      className="w-full h-8 text-sm border rounded-md px-2 bg-white"
                    >
                      <option value="vaginal">Vaginal</option>
                      <option value="c_section">C-Section</option>
                      <option value="vbac">VBAC</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Notes</label>
                  <Input
                    placeholder="Optional"
                    value={newLine.notes || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setNewLine((l) => ({ ...l, notes: e.target.value }))
                    }
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddLineItem} disabled={actionLoading || !newLine.service_date}>
                  {actionLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add
                </Button>
                <Button size="sm" variant="outline" onClick={() => {
                  setShowAddLine(false);
                  setNewLine({ line_type: 'prenatal_visit', service_date: '' });
                }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Line items table */}
          {lineItems.length === 0 ? (
            <div className="text-center p-6 text-gray-400">
              <Activity className="w-8 h-8 mx-auto mb-2" />
              <p>No visit line items yet. Add prenatal visits, postpartum visits, or deliveries.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {lineItems.map((line) => {
                const typeCfg = LINE_TYPE_CONFIG[line.line_type] || LINE_TYPE_CONFIG.prenatal_visit;
                return (
                  <div
                    key={line.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md border"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${typeCfg.color}`}>
                        {typeCfg.icon}
                        {typeCfg.label}
                      </span>
                      <div className="flex items-center gap-1 text-sm text-gray-700">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {formatDateOnly(line.service_date)}
                      </div>
                      {line.delivery_type && (
                        <span className="text-xs text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded">
                          {DELIVERY_TYPE_LABELS[line.delivery_type] || line.delivery_type}
                        </span>
                      )}
                      {line.notes && (
                        <span className="text-xs text-gray-400">{line.notes}</span>
                      )}
                    </div>
                    {!isTerminal && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteLineItem(line.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        disabled={actionLoading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick add buttons */}
          {!isTerminal && !showAddLine && (
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewLine({ line_type: 'prenatal_visit', service_date: '' });
                  setShowAddLine(true);
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                Prenatal Visit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewLine({ line_type: 'postpartum_visit', service_date: '' });
                  setShowAddLine(true);
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                Postpartum Visit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewLine({ line_type: 'delivery', service_date: '', delivery_type: 'vaginal' });
                  setShowAddLine(true);
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                Delivery
              </Button>
            </div>
          )}
        </Card>

        {/* Claim Created Banner */}
        {selectedClaim.status === 'claim_created' && selectedClaim.claim_number && (
          <Card className="p-4 bg-emerald-50 border-emerald-200">
            <div className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Draft Claim Created</p>
                <p className="text-sm text-emerald-600">
                  Claim #{selectedClaim.claim_number}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 text-emerald-700"
                    onClick={() => onNavigate?.('billing-claims')}
                  >
                    View in Claims Management
                  </Button>
                </p>
              </div>
            </div>
          </Card>
        )}
      </>
    );
  };

  // ---- Main Render ----

  return (
    <div>
      {view === 'list' && renderList()}
      {view === 'create' && renderCreate()}
      {view === 'detail' && renderDetail()}
    </div>
  );
}
